import type { ComponentChild, FunctionComponent } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import type { TemplateContextMap, TemplateFunction } from 'Components/types';
// import { vehicleColorUpdated } from 'Core/actions/fitmentSearch';
import { selectedVehicleColorIdSelector } from 'Core/reducers/fitmentSearch/storage';
import { searchResponseExtraSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import {
  type AutoSyncConfig,
  type AutoSyncPartLoader,
  type AutoSyncVehicleOptions,
  type AutoSyncVisualizer,
  isAutoSyncVisualizer,
} from 'Utils/autoSync';
import isTruthy from 'Utils/isTruthy';

export type {
  AutoSyncConfig,
  AutoSyncPartLoader,
  AutoSyncVehicleOptions,
  AutoSyncVisualizer,
  InitVvsExpressProps,
} from 'Utils/autoSync';
export { createAutoSyncPartLoader } from 'Utils/autoSync';

const configDefaults: AutoSyncConfig = {
  height: '110px',
  vehicleImageSize: 640,
  vehicleAngles: [32],
  showColorPicker: false,
};
const vehicleVisualizedClass = 'cm_AutoSync-visualized';

export interface Props {
  template: TemplateFunction<Params>;
  toggleGridButtonVisibility?: boolean;
  config?: AutoSyncConfig;
  autoSyncPartNumber?: string;
  partLoader?: AutoSyncPartLoader;
  extraParams?: TemplateContextMap;
}

export type Params = {
  visualization: ComponentChild;
  isVehicleError: boolean;
  isVisualizationError: boolean;
} & TemplateContextMap;

const AutoSyncVisualization: FunctionComponent<Props> = ({
  template,
  toggleGridButtonVisibility = true,
  config,
  autoSyncPartNumber,
  partLoader,
  extraParams,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const visualizerRef = useRef<AutoSyncVisualizer>();
  const visualizerConfigRef = useRef<AutoSyncConfig>({ ...configDefaults, ...(config ?? {}) });
  const toggleGridButtonVisibilityRef = useRef(toggleGridButtonVisibility);

  // const dispatch = useDispatch();

  const [isVehicleError, setIsVehicleError] = useState(false);
  const [isVisualizationError, setIsVisualizationError] = useState(false);
  const [isVisualizationHidden, setIsVisualizationHidden] = useState(false);

  const vehicleIdRaw = useSelector(searchResponseExtraSelector)?.AutoSyncVehicleId;
  // The wire format historically permits both `string` and `number` ids;
  const vehicleId = vehicleIdRaw == null ? undefined : String(vehicleIdRaw);
  const colorId = useSelector(selectedVehicleColorIdSelector);

  const visualizeVehicle = useCallback(() => {
    if (!vehicleId) {
      return;
    }

    if (!visualizerRef.current) {
      const candidate = window.initVvsExpress?.({
        apiUrl: 'https://api.autosyncstudio.com/',
        apiKey: fitmentSearchConfig.autoSyncKey ?? '',
        el: containerRef.current,
        ...visualizerConfigRef.current,
      });
      // The third-party `initVvsExpress` is not under our control;
      // if it ever ships a build whose return value is missing one of the methods
      // we call below, bail out instead of crashing on an undefined invocation later.
      visualizerRef.current = isAutoSyncVisualizer(candidate) ? candidate : undefined;
    }

    if (!visualizerRef.current) {
      return;
    }

    if (toggleGridButtonVisibilityRef) {
      window.document.body.classList.remove(vehicleVisualizedClass);
    }

    const options: AutoSyncVehicleOptions = { ...partLoader?.vehicleOptions };
    if (colorId) {
      options.colorId = +colorId;
    }

    // Vehicle must finish loading before tire/wheel PN — especially required for tires.
    // https://vvs.autosyncstudio.com/express/docs/#examples/example-loading-tires-code.html
    tryVisualizeVehicle(visualizerRef, options, Number(vehicleId))
      .then(() => {
        setIsVehicleError(false);
        setIsVisualizationHidden(false);

        // IMPORTANT: temporary Color Update feature is disabled, because something is wrong with the AutoSync API
        // visualizerRef.current?.onVehicleColorChange((color: Color) => {
        //   if (color.Name && color.Id) {
        //     dispatch(
        //       vehicleColorUpdated({
        //         vehicle: selectedVehicle,
        //         vehicleExtra: {
        //           colorId: color.Id.toString(),
        //         },
        //       }),
        //     );
        //   }
        // });

        if (toggleGridButtonVisibilityRef) {
          window.document.body.classList.add(vehicleVisualizedClass);
        }

        if (!autoSyncPartNumber || !visualizerRef.current || !partLoader) {
          return;
        }

        return partLoader
          .load(visualizerRef.current, autoSyncPartNumber)
          .then(() => {
            setIsVisualizationError(false);
          })
          .catch((err: Error) => {
            setIsVisualizationError(true);
            console.warn(`Failed to visualize the '${autoSyncPartNumber}' part. ${err.message}`);
          });
      })
      .catch((err: Error) => {
        setIsVehicleError(true);
        setIsVisualizationHidden(true);
        console.warn(err.message);
      });
  }, [autoSyncPartNumber, partLoader, colorId, vehicleId]);

  useEffect(() => {
    try {
      visualizeVehicle();
    } catch (error) {
      console.error('Failed to visualize a vehicle', error);
    }
  }, [visualizeVehicle]);

  useEffect(() => {
    const height = isVisualizationHidden ? '0px' : (visualizerConfigRef.current?.height ?? '110px');
    try {
      visualizerRef.current?.setHeight(height);
    } catch (error) {
      console.error('Failed to set height', error);
    }
  }, [isVisualizationHidden]);

  if (!vehicleId) {
    return null;
  }

  const visualization = (
    <div
      ref={containerRef}
      className={['cm_vehicle-visualization', isVehicleError && 'cm_hide'].filter(isTruthy).join(' ')}
    ></div>
  );

  return template.call({ visualization, isVehicleError, isVisualizationError, ...extraParams });
};

async function tryVisualizeVehicle(
  visualizerRef: { current: AutoSyncVisualizer | undefined },
  options: AutoSyncVehicleOptions,
  vehicleId: number,
) {
  try {
    await visualizerRef.current?.loadVehicleById(vehicleId, options);
  } catch {
    const message = `Failed to visualize the vehicle. Vehicle ID: ${vehicleId}`;
    console.warn(message);
    throw new Error(message);
  }
}

export default AutoSyncVisualization;
